/* eslint-disable max-lines */
import { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { fmtDateTime } from '@utils/format';
import toast from 'react-hot-toast';
import {
  Download,
  FileText,
  Clock,
  CheckCircle2,
  XCircle,
  Trash2
} from 'lucide-react';
import { Badge } from '@components/atoms/ui-kit/badge';
import { Card } from '@components/atoms/ui-kit/card';
import { Text } from '@components/atoms/ui-kit/text';
import { EmptyState } from '@components/atoms/ui-kit/empty-state';

import { usePageHeader } from '@utils/hooks/use-page-header';
import DataGridV2, {
  type DataGridColumn
} from '@components/atoms/datatable/data-grid-v2';
import type { GridAction } from '@components/atoms/datatable/grid-action-controller';

import { useGetExportTemplateItems } from '@utils/api/organizations/use-get-export-template-items';
import type { PagedListParams } from '@utils/api/endpoints.paged.ts.tsx';
import { useNewAppSearchParams } from '@utils/hooks/use-new-app-search-params.ts';
import { useGetExportTemplateItemDownload } from '@utils/api/organizations/use-get-export-template-item-download';
import { useDeleteExportTemplateItem } from '@utils/api/organizations/use-delete-export-template-item';
import {
  ExportTemplateItem,
  ExportTemplateItemStatus
} from '@utils/types/organization/export-template-item';
import Modal from '@components/atoms/modals/modal';
import Button from '@components/atoms/ui-kit/button';

type Row = {
  id: number;
  status: ExportTemplateItemStatus;
  template_name: string;
  document_filename: string;
  created_by_full_name: string;
  created_at: string;
  date_finished: string | null;
  has_file: boolean;
  error_message: string | null;
};

const DEFAULT_PAGE_SIZE = 20;

export default function ExportTemplateItems() {
  const { t } = useTranslation();
  const { searchParams } = useNewAppSearchParams();
  const qString = (searchParams.q as string | undefined) || undefined;

  const [downloadingItemId, setDownloadingItemId] = useState<number | null>(
    null
  );
  const [deleteConfirmItem, setDeleteConfirmItem] = useState<Row | null>(null);
  const [pagination, setPagination] = useState({
    pageIndex: 0,
    pageSize: DEFAULT_PAGE_SIZE
  });
  const [sorting, setSorting] = useState<{ id: string; desc: boolean }[]>([
    { id: 'created_at', desc: true }
  ]);

  const paginationParams: PagedListParams = {
    offset: pagination.pageIndex * pagination.pageSize,
    limit: pagination.pageSize,
    sort: sorting[0]?.id ?? 'created_at',
    direction: sorting[0]?.desc ? 'desc' : 'asc',
    q: qString
  };

  const { data, isLoading } = useGetExportTemplateItems(paginationParams);
  const downloadMutation = useGetExportTemplateItemDownload();
  const deleteMutation = useDeleteExportTemplateItem();

  const rows = useMemo<Row[]>(() => {
    if (!data?.result) return [];
    return data.result.map((item: ExportTemplateItem) => ({
      id: item.id,
      status: item.status,
      template_name: item.export_template_name,
      document_filename: item.document_filename,
      created_by_full_name: item.created_by_full_name,
      created_at: item.created_at,
      date_finished: item.date_finished,
      has_file: item.has_file,
      error_message: item.error_message
    }));
  }, [data]);

  const handleDownload = async (itemId: number, filename: string) => {
    setDownloadingItemId(itemId);
    const toastId = toast.loading(t('generating-download-link'));

    try {
      const response = await downloadMutation.mutateAsync({
        itemId,
        expirySeconds: 3600
      });

      toast.dismiss(toastId);

      // Trigger browser download
      const link = document.createElement('a');
      link.href = response.download_url;
      link.download = response.filename || filename;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);

      toast.success(t('download-started'));
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
    } catch (error: any) {
      toast.dismiss(toastId);

      if (error?.response?.status === 400) {
        const errorCode = error?.response?.data?.error_code;
        if (errorCode === 'no-file-available') {
          toast.error(t('file-not-available'));
        } else {
          toast.error(t('error-downloading-file'));
        }
      } else if (error?.response?.status === 404) {
        toast.error(t('export-not-found'));
      } else {
        toast.error(t('error-downloading-file'));
      }
    } finally {
      setDownloadingItemId(null);
    }
  };

  const handleDelete = async (item: Row) => {
    const toastId = toast.loading(t('deleting'));

    try {
      await deleteMutation.mutateAsync(item.id);
      toast.dismiss(toastId);
      toast.success(t('export-item-deleted'));
      setDeleteConfirmItem(null);
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
    } catch (error: any) {
      toast.dismiss(toastId);

      if (error?.response?.status === 404) {
        toast.error(t('export-not-found'));
      } else if (error?.response?.status === 400) {
        toast.error(
          error?.response?.data?.error || t('cannot-delete-export-item')
        );
      } else {
        toast.error(t('error-deleting-export-item'));
      }
    }
  };

  const getStatusBadge = (status: ExportTemplateItemStatus) => {
    const colorMap: Record<
      ExportTemplateItemStatus,
      'zinc' | 'blue' | 'green' | 'red'
    > = {
      [ExportTemplateItemStatus.NOT_RUNNING]: 'zinc',
      [ExportTemplateItemStatus.RUNNING]: 'blue',
      [ExportTemplateItemStatus.FINISHED]: 'green',
      [ExportTemplateItemStatus.ERROR]: 'red'
    };
    const iconMap = {
      [ExportTemplateItemStatus.NOT_RUNNING]: <Clock className="h-3 w-3" />,
      [ExportTemplateItemStatus.RUNNING]: (
        <Clock className="h-3 w-3 animate-spin" />
      ),
      [ExportTemplateItemStatus.FINISHED]: <CheckCircle2 className="h-3 w-3" />,
      [ExportTemplateItemStatus.ERROR]: <XCircle className="h-3 w-3" />
    };
    const labelMap = {
      [ExportTemplateItemStatus.NOT_RUNNING]: t('queued'),
      [ExportTemplateItemStatus.RUNNING]: t('processing'),
      [ExportTemplateItemStatus.FINISHED]: t('completed'),
      [ExportTemplateItemStatus.ERROR]: t('failed')
    };

    return (
      <Badge
        color={colorMap[status] ?? 'zinc'}
        className="inline-flex items-center gap-1.5"
      >
        {iconMap[status] ?? iconMap[ExportTemplateItemStatus.NOT_RUNNING]}
        {labelMap[status] ?? labelMap[ExportTemplateItemStatus.NOT_RUNNING]}
      </Badge>
    );
  };

  const actions = useMemo<GridAction<Row>[]>(() => {
    return [
      {
        id: 'download',
        label: t('download'),
        icon: <Download className="h-3.5 w-3.5" />,
        run: async row => {
          if (
            row.status === ExportTemplateItemStatus.FINISHED &&
            row.has_file
          ) {
            await handleDownload(row.id, row.document_filename);
          } else if (row.status === ExportTemplateItemStatus.ERROR) {
            toast.error(row.error_message || t('failed'));
          } else if (row.status === ExportTemplateItemStatus.RUNNING) {
            toast.error(t('export-not-ready'));
          } else {
            toast.error(t('file-not-available'));
          }
        },
        disabled: (row: Row) =>
          row.status !== ExportTemplateItemStatus.FINISHED || !row.has_file
      },
      {
        id: 'delete',
        label: t('delete'),
        icon: <Trash2 className="h-3.5 w-3.5" />,
        intent: 'danger',
        run: async row => {
          setDeleteConfirmItem(row);
        },
        variant: 'danger',
        // TODO: Enable when backend DELETE endpoint is ready
        disabled: () => true
      }
    ];
  }, [t]);

  const columns = useMemo<DataGridColumn<Row>[]>(() => {
    return [
      {
        id: 'status',
        header: t('status'),
        field: 'status',
        minWidth: 120,
        maxWidth: 150,
        enableSorting: true,
        enableFiltering: true,
        filterVariant: 'select',
        filterKey: 'status',
        serverOperator: 'eq',
        filterOptions: [
          { label: t('queued'), value: ExportTemplateItemStatus.NOT_RUNNING },
          { label: t('processing'), value: ExportTemplateItemStatus.RUNNING },
          { label: t('completed'), value: ExportTemplateItemStatus.FINISHED },
          { label: t('failed'), value: ExportTemplateItemStatus.ERROR }
        ],
        cell: ({ getValue }) =>
          getStatusBadge(getValue() as ExportTemplateItemStatus)
      },
      {
        id: 'template_name',
        header: t('template'),
        field: 'template_name',
        minWidth: 150,
        enableSorting: false,
        enableFiltering: false,
        cell: ({ getValue }) => (
          <div className="flex items-center gap-2">
            <FileText className="h-4 w-4 flex-shrink-0" />
            <Text as="span" size="sm" weight="medium" className="truncate">
              {String(getValue() ?? '-')}
            </Text>
          </div>
        )
      },
      {
        id: 'document_filename',
        header: t('document-name'),
        field: 'document_filename',
        minWidth: 200,
        maxWidth: 300,
        enableSorting: false,
        enableFiltering: true,
        filterVariant: 'text',
        filterKey: 'document_filename',
        serverOperator: 'ilike',
        cell: ({ getValue }) => {
          const filename = String(getValue() ?? '-');
          return (
            <Text
              as="div"
              size="sm"
              tone="muted"
              className="truncate"
              style={{ maxWidth: '400px' }}
              title={filename}
            >
              {filename}
            </Text>
          );
        }
      },
      {
        id: 'created_by_full_name',
        header: t('created-by'),
        field: 'created_by_full_name',
        minWidth: 150,
        enableSorting: false,
        enableFiltering: true,
        filterVariant: 'text',
        filterKey: 'created_by_name',
        serverOperator: 'ilike',
        cell: ({ getValue }) => (
          <Text as="span" size="sm">
            {String(getValue() ?? '-')}
          </Text>
        )
      },
      {
        id: 'created_at',
        header: t('started'),
        field: 'created_at',
        minWidth: 150,
        enableSorting: true,
        enableFiltering: false,
        cell: ({ getValue }) => {
          const date = getValue();
          if (!date) return '-';
          try {
            return (
              <Text as="span" size="sm" tone="muted">
                {fmtDateTime(String(date))}
              </Text>
            );
          } catch {
            return String(date);
          }
        }
      },
      {
        id: 'date_finished',
        header: t('finished'),
        field: 'date_finished',
        minWidth: 150,
        enableSorting: true,
        enableFiltering: false,
        cell: ({ getValue }) => {
          const date = getValue();
          if (!date) return '-';
          try {
            return (
              <Text as="span" size="sm" tone="muted">
                {fmtDateTime(String(date))}
              </Text>
            );
          } catch {
            return String(date);
          }
        }
      }
    ];
  }, [t]);

  usePageHeader({
    title: t('export-history'),
    description: t('view-and-download-your-exported-files')
  });

  return (
    <>
      <div className="pb-4 sm:pb-0 relative">
        <DataGridV2<Row>
          data={rows}
          columns={columns}
          actions={actions}
          striped
          dense
          grid={false}
          height="88vh"
          enablePagination
          manualPagination
          totalItems={data?.total_results ?? 0}
          onPaginationChange={setPagination}
          loading={isLoading}
          enableSorting
          manualSorting
          onSortingChange={setSorting}
          enableFiltering
          manualFiltering
          pageSize={pagination.pageSize}
          pageSizeOptions={[25, 50, 75, 100]}
          enableRowSelection={false}
          showColumnFilters={false}
          filterMode="popover"
        />

        {rows.length === 0 && !isLoading && !!data && !qString && (
          <div className="absolute inset-0 flex items-center justify-center pointer-events-none">
            <EmptyState
              icon={<FileText className="h-7 w-7" />}
              title={t('no-exports-found')}
              subtitle={t('no-exports-found-description')}
            />
          </div>
        )}
      </div>

      {downloadingItemId && (
        <div className="fixed bottom-4 right-4 bg-app-soft shadow-lg rounded-2xl p-4 border border-hairline">
          <div className="flex items-center gap-3">
            <div className="h-5 w-5 animate-spin rounded-full border-2 border-solid border-current border-r-transparent" />
            <Text as="span" size="sm" weight="medium">
              {t('preparing-download')}
            </Text>
          </div>
        </div>
      )}

      {/* Delete Confirmation Modal */}
      <Modal
        isOpen={!!deleteConfirmItem}
        onClose={() => setDeleteConfirmItem(null)}
        title={t('delete-export')}
        size="small"
        footerActions={[
          <Button
            key="cancel"
            label={t('cancel')}
            size="small"
            variant="smoke"
            onClick={() => setDeleteConfirmItem(null)}
            disabled={deleteMutation.isLoading}
          />,
          <Button
            key="delete"
            label={t('delete')}
            size="small"
            disabled={true}
            variant="danger"
            onClick={() => deleteConfirmItem && handleDelete(deleteConfirmItem)}
            loading={deleteMutation.isLoading}
          />
        ]}
      >
        <Text as="p" size="sm" tone="muted">
          {t('delete-export-confirmation')}
        </Text>
        {deleteConfirmItem && (
          <Card padding="none" className="mt-3 p-3">
            <Text as="p" size="sm" weight="medium">
              {deleteConfirmItem.document_filename}
            </Text>
            <Text as="p" size="xs" tone="muted" className="mt-1">
              {t('template')}: {deleteConfirmItem.template_name}
            </Text>
          </Card>
        )}
      </Modal>
    </>
  );
}
